"use client"
import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react"
import { X } from "lucide-react"
import type {
  ExtrapolatedGraphScaleOverrides,
} from "@/app/playbook/extrapolated-graph/types"
import {
  HudContextProvider,
  HudGlobeBlock,
  HudExtrapolatedGraphBlock,
  PRIMARY_GRAPH_HEIGHT_PX,
  type PlaybookHUDProps,
} from "../hud/shared/playbookHUDInfra"
import {
  HudPersistentGlobeAndGraph,
} from "../hud/blocks/layout/HudPersistentGlobeAndGraph"
// The expanded dashboard renders desktop HUD variant 0's ACTUAL body pieces
// (BelowDetailHudPanels inside BelowDetailHudSwapProviders, same opts) so the
// two can't drift: every hover-swap interaction the desktop HUD gains —
// capability ladder, power breakdown, policy map, labor detail, metric
// chips + details — works here too (touch taps drive the same enter events).
import {
  BELOW_DETAIL_HUD_BODY_OPTS,
  BelowDetailHudPanels,
  BelowDetailHudSwapProviders,
} from "../hud/variants"
import {
  CapabilityHoverBridge,
} from "../hud/blocks/capability/capabilityHoverSwap"
import {
  PolicyLayoutAnimationDisabledContext,
} from "../hud/blocks/policy/policyLayoutAnimationContext"
import { HoverSwapsDisabledContext } from "../hud/blocks/hoverSwapsDisabled"
import {
  BandHudOverlayProvider,
} from "../extrapolated-graph/BandHudOverlayChannel"
import {
  type MobileHudDesignId,
  HUD87_CAPABILITY_PAST_LINE_COLOR,
  HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX,
  HUD87_CAPABILITY_LINE_FLOOR_VALUE,
  HUD87_CURRENT_POINT_DOT_RADIUS_PX,
  HUD87_GRAPH_MINIMUM_DATE,
} from "../hud/variants"
import {
  DEFAULT_SCALE_SETTINGS,
  GRAPH_LINE_STROKE_WIDTH,
  PLAN_KEYS,
  defaultGraphData,
} from "../extrapolated-graph/graphConstants"
import {
  MobileCapabilityLineChart,
  type CapabilityPoint,
} from "./MobileCapabilityLineChart"
import { MobileCompactHudStrip } from "./mobile-strip/MobileCompactHudStrip"
import { MobileStripGlobeLayer } from "./mobile-strip/MobileStripGlobeLayer"
import { COMPACT_STRIP_VARIANTS } from "./mobile-strip/stripVariants"
import { MobileMetricDetailsPanel } from "../hud/metric-details/MetricDetailsPanel"
import {
  type MetricPanelDesignId,
} from "../hud/metric-details/panel-redesigns/metricPanelRedesigns"
import { useAnimatedDate } from "../hooks/useAnimatedDate"
import { useAnimationTuning } from "../animation/animationTuningContext"
import { useAudio } from "@/app/contexts/AudioContext"
import {
  MobileHudAudioBar,
  MOBILE_HUD_AUDIO_BAR_HEIGHT_PX,
} from "@/app/audio/MobileHudAudioBar"

const MOBILE_GRAPH_BLEED_PX = 16
const MOBILE_PREVIEW_HEIGHT_PX = 72
const MOBILE_PREVIEW_GRAPH_HEIGHT_PX = 68
const MOBILE_EXPANDED_GRAPH_HEIGHT_PX = 320
const MOBILE_EXPANDED_GLOBE_SIZE_PX = 300
const MOBILE_GLOBE_MIN_DEVICE_PIXEL_RATIO = 2
const MOBILE_HUD_TRANSITION_MS = 300
const MOBILE_PREVIEW_TAP_CANCEL_DISTANCE_PX = 8
// Container-transform between the bottom bubble and the full-screen
// dashboard: a JS tween re-renders the graph each frame at interpolated
// geometry while the overlay's clip window opens from the bubble's rect —
// every frame is a real graph, no scaling artifacts. Duration is tuned:
// mobileSheetMorphMs (animationTuningDefaults, edited on /zoom-editor and
// /hud-studio).
// Sheet drag (strip variants with expandHandle.draggable): grabber hotspot
// geometry + gesture tuning.
const SHEET_DRAG_HOTSPOT_WIDTH_EXTRA_PX = 48 // hotspot width beyond the bar
const SHEET_DRAG_HOTSPOT_HEIGHT_PX = 30
const SHEET_DRAG_START_THRESHOLD_PX = 6 // upward travel before the morph arms
const SHEET_DRAG_OPEN_PROGRESS = 0.3 // released past this → snaps open
const SHEET_DRAG_FLICK_VELOCITY_PX_PER_MS = 0.4 // upward flick opens anywhere
// Sheet mode's full-open state is a floating overlay CARD, not a page
// takeover: the pane keeps the strip's width/margins/radius and its top
// edge stops this far below the viewport top.
const SHEET_CARD_TOP_GAP_PX = 56
const SHEET_CARD_SCRIM_OPACITY = 0.25 // page dim behind the full-open card
// Dismiss drag (the card's top pill pulled down): released below this
// progress → the sheet closes; above it → it springs back open.
const SHEET_DISMISS_CLOSE_PROGRESS = 0.7
// Gap between the card's top edge and its grabber pill. The card content
// starts 14px in (the wrapper's paddingTop), so 22 puts the pill 8px INTO
// the graph — it sits on the graph's dither field, knocked out in the page
// background color exactly like the strip's pill.
const SHEET_CARD_HANDLE_TOP_PX = 22
// Tappable square around the card's 36px close ✕ (the visible circle keeps
// its size; the hotspot is centered on it).
const SHEET_CLOSE_HOTSPOT_PX = 56

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3
const lerp = (from: number, to: number, t: number) => from + (to - from) * t
// morphProgress at which the sheet's top edge sits at (1 - viewportFraction)
// of the screen — the half-detent resting point, derived from the same lerp
// that drives the clip's top inset (originTop -> SHEET_CARD_TOP_GAP_PX).
const sheetHalfDetentProgress = (originTop: number, viewportFraction: number) =>
  Math.min(1, Math.max(0,
    (originTop - window.innerHeight * (1 - viewportFraction))
      / Math.max(1, originTop - SHEET_CARD_TOP_GAP_PX)))
// The in-flow block at the top of the mobile page shows the desktop HUD's
// globe+graph section, CSS-scaled from its native 455px frame to the block's
// own width — the block stays inside the page container, so its edges line
// up with the story text's padding. The globe container's small left/top
// overhangs and the projected line's right overhang run harmlessly into the
// page margins, as they do on desktop.
const DESKTOP_TOP_GRAPH_WIDTH_PX = 455
// The bottom bubble fades in once the top graph's bottom edge has scrolled
// this many px past the top of the viewport.
const MOBILE_BUBBLE_FADE_TRIGGER_PX = 12
// Scrolled-state bubble chrome (ai-2027-style): the bottom graph sits in a
// rounded, bordered card with a margin from the screen edges instead of a
// full-bleed strip.
const MOBILE_BUBBLE_MARGIN_PX = 10
const MOBILE_BUBBLE_RADIUS_PX = 24

// The default 4px capabilities line overpowers the short bubble; the new
// collapsed chart scales it down by this factor.
const MOBILE_BUBBLE_LINE_STROKE_SCALE = 0.7
// Tight transition window for the mobile graph (bubble + expanded
// dashboard) — bounded and eased, vs the desktop graph's slow smoothing.
const MOBILE_GRAPH_ANIMATION_MS = 500

type MobileCapabilitiesGraphProps = {
  width: number
  height: number
  // Render exactly like the desktop side-panel graph (PrimaryGraph in
  // hud/blocks/graph — keep the two in sync): default full-timeline
  // viewport with the projected future window, no milestone rings or
  // milestone-year axis mode. Off = the bubble's action-fit rendering.
  desktopRendering?: boolean
  showLabels?: boolean
  revealAnnotations?: boolean
  showRiskBackgroundLabels?: boolean
  scaleOverrides?: ExtrapolatedGraphScaleOverrides
  paused?: boolean
  showRiskGradient?: boolean
  // Capability milestone labels on the curve ("Reliable Agent", …). The
  // overlay drops labels that don't fit the plot bounds or overlap each
  // other, so this is a budget, not a promise — 3 lets several reached
  // milestones coexist instead of the newest fading the previous one out.
  numCapabilityThresholdLabels?: number
  // False for the pointer-inert previews so they aren't focusable triggers.
  detailsTrigger?: boolean
}

// NOTE: after the merge with main's graph refactor, HudExtrapolatedGraphBlock
// no longer accepts the mobile-only tuning props this wrapper used to pass
// (animationDurationMs, fitViewportToAction, disableViewportTransition,
// lineStrokeWidthScale, milestone/axis toggles). They were dropped here to
// track main's API; the morph/expanded graph now uses main's defaults. If the
// snappy timing / viewport-snap / stroke-scaling need to come back, they must
// be re-added onto main's refactored pipeline.
const MobileCapabilitiesGraph = ({
  width,
  height,
  desktopRendering = false,
  showLabels = false,
  revealAnnotations = false,
  showRiskBackgroundLabels = false,
  scaleOverrides,
  paused,
  showRiskGradient = true,
  numCapabilityThresholdLabels = 3,
  detailsTrigger,
}: MobileCapabilitiesGraphProps) => (
  <HudExtrapolatedGraphBlock
    width={width}
    height={height}
    numCapabilityThresholdLabels={
      desktopRendering ? 0 : numCapabilityThresholdLabels
    }
    lossOfControlHoverRevealOverride={revealAnnotations}
    useAnimatedDateForViewport={!desktopRendering}
    animateViewportScale={false}
    // desktopRendering = the expanded dashboard: match desktop HUD variant 0's
    // capability-line styling exactly (dark-red past line, current-point dot +
    // label, graph floor/min-date, corner year pill; no inline AI-CAPABILITIES
    // label — variant 0 omits it). The bubble/morph render (desktopRendering
    // off) keeps its own lighter styling.
    showAICapabilitiesLabel={desktopRendering ? false : showLabels}
    currentYearPill={desktopRendering}
    capabilityPastLineColor={desktopRendering ? HUD87_CAPABILITY_PAST_LINE_COLOR : undefined}
    capabilityPastLineStrokeWidth={desktopRendering ? HUD87_CAPABILITY_PAST_LINE_STROKE_WIDTH_PX : undefined}
    capabilityLineFloorValue={desktopRendering ? HUD87_CAPABILITY_LINE_FLOOR_VALUE : undefined}
    showCurrentPointMarker={desktopRendering}
    currentPointMarkerRadius={desktopRendering ? HUD87_CURRENT_POINT_DOT_RADIUS_PX : undefined}
    showCurrentPointCapabilityLabel={desktopRendering}
    minimumGraphDate={desktopRendering ? HUD87_GRAPH_MINIMUM_DATE : undefined}
    // Desktop parity: the line hover explains the graph via the capability
    // ladder takeover, so the whole-graph explainer card is redundant.
    hideGraphExplainerTooltip={desktopRendering}
    redAboveLossOfControl={showRiskGradient}
    // The projected takeover-risk line is part of the same risk layer as
    // the gradient — the floating-line bubble drops both.
    showLossOfControlLines={showRiskGradient}
    showAllYears={false}
    showYearDots={false}
    showRiskBackgroundLabels={showRiskBackgroundLabels}
    scaleOverrides={scaleOverrides}
    paused={paused}
    detailsTrigger={detailsTrigger}
  />
)

export const MobileHUD = ({
  design = "bubble",
  isDateHoverAutoplay = false,
  metricPanelDesign = null,
  ...props
}: PlaybookHUDProps & {
  design?: MobileHudDesignId
  // True while a plan-button hover/tap autoplay is sweeping the story date
  // (useBranchHoverAutoplayDate) — the strip holds its frozen frame then.
  isDateHoverAutoplay?: boolean
  // Details-panel treatment for the active HUD tab (HUD_METRIC_PANEL_DESIGNS),
  // same switch the desktop column gets; null = production content.
  metricPanelDesign?: MetricPanelDesignId | null
}) => {
  // Reader is in the epilogue (same derivation as the desktop HUD's
  // isEpilogueHudMode) — the whole bottom HUD fades out for the ending and
  // fades back if they scroll up out of it. Placeholder treatment: nothing
  // bespoke is designed for the epilogue's bottom bar yet, so for now the
  // ending just gets a clean screen.
  const isEpilogueEngaged =
    !!props.isEpilogueExpanded && !!props.isEpilogueScrolledTo
  // Strip tabs read their look from COMPACT_STRIP_VARIANTS; the classic
  // bubble keeps its own constants (null config).
  const stripConfig = design === "bubble" ? null : COMPACT_STRIP_VARIANTS[design]
  // Non-null when this variant's grabber bar is a real drag handle — the
  // sheet-drag hotspot mounts over it (see beginSheetDrag).
  const sheetDragHandle =
    stripConfig?.expandHandle?.draggable === true ? stripConfig.expandHandle : null
  // Sheet mode (draggable variants): the overlay is an OPAQUE pane whose
  // rising edge reveals the dashboard — content sits on the pane (translated
  // to its top edge) at full opacity the whole way. No morph-graph
  // crossfade, no tint fade: a slow finger drag must never show the page
  // and the dashboard blended through each other.
  const isSheetMode = sheetDragHandle !== null
  // Half-detent config (sheetHalfDetent variants) and the morphProgress of
  // its resting point, captured per gesture (it depends on the origin rect
  // and viewport, both measured at gesture start).
  const sheetHalfDetent = stripConfig?.sheetHalfDetent ?? null
  const sheetHalfTargetRef = useRef(0.5)
  // Where a released sheet drag settles when the variant has a half detent:
  // a flick advances toward the flick's direction (up: half then full; down:
  // half then closed); otherwise the nearest detent by progress wins.
  const chooseSheetDetentPhase = (
    progress: number,
    velocityUpPxPerMs: number,
  ): "closing" | "half-anim" | "open" => {
    const halfP = sheetHalfTargetRef.current
    if (velocityUpPxPerMs > SHEET_DRAG_FLICK_VELOCITY_PX_PER_MS)
      return progress >= halfP ? "open" : "half-anim"
    if (velocityUpPxPerMs < -SHEET_DRAG_FLICK_VELOCITY_PX_PER_MS)
      return progress <= halfP ? "closing" : "half-anim"
    if (progress < halfP * 0.5) return "closing"
    if (progress < halfP + (1 - halfP) * 0.5) return "half-anim"
    return "open"
  }
  // Height of the fixed bottom element: strip variants bring their own
  // budget (stripHeightPx, production 76); the classic bubble keeps 72px.
  const previewHeightPx =
    stripConfig !== null ? stripConfig.stripHeightPx : MOBILE_PREVIEW_HEIGHT_PX
  // Narration row (play/scrub/speed — MobileHudAudioBar) under the strip
  // graph, present only once the reader has opened the audio UI (the byline's
  // "Listen" button; AudioContext.isVisible persists it). The card grows by
  // its height and the expand hotspots shift up by the same amount, so they
  // keep covering exactly the graph and the row stays independently tappable.
  const { isVisible: isAudioUiOpen } = useAudio()
  const audioBarHeightPx = isAudioUiOpen ? MOBILE_HUD_AUDIO_BAR_HEIGHT_PX : 0
  const previewMarginPx = stripConfig?.marginPx ?? MOBILE_BUBBLE_MARGIN_PX
  const previewRadiusPx = stripConfig?.radiusPx ?? MOBILE_BUBBLE_RADIUS_PX
  const previewPointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const didPreviewPointerMoveRef = useRef(false)
  // The story date snaps between keyframes; smooth it (420ms cubic, same hook
  // the rest of the HUD uses) so the bubble chart — which renders directly
  // from this date with no animation of its own — pans smoothly.
  const bubbleChartDate = useAnimatedDate(props.currentDate)
  // Tuned duration of the collapsed-pill ↔ expanded-sheet morph (see
  // animationTuningDefaults; edited on /zoom-editor and /hud-studio).
  const sheetMorphMs = useAnimationTuning().mobileSheetMorphMs
  // The full capability line (shared history + the active plan's future) for
  // the collapsed bubble chart, assembled the same way the desktop pipeline
  // does (see useExtrapolatedGraphRenderProps: shared + PLAN_KEYS[plan]).
  const capabilityPoints = useMemo<CapabilityPoint[]>(() => {
    const future =
      props.activePlan === "B" && defaultGraphData.planBUs?.length
        ? defaultGraphData.planBUs
        : defaultGraphData[PLAN_KEYS[props.activePlan]] ?? defaultGraphData.planD
    return [...defaultGraphData.shared, ...future].map(datum => ({
      date: new Date(datum.date),
      value: datum.value,
    }))
  }, [props.activePlan])
  // closed (overlay pre-mounted but hidden) -> open (a JS tween drives
  // morphProgress 0->1: the morph graph re-renders each frame at genuinely
  // interpolated dimensions — width, height, padding, stroke — so every
  // frame is a coherent graph, no scaling/squashing; the clip window is
  // derived from the same progress) -> settled (the in-flow dashboard graph
  // takes over, unpauses, labels and globe fade in) -> closing (reverse
  // tween) -> closed. Sheet-drag variants add "dragging" before open/closing:
  // the finger drives morphProgress directly (no tween), and release hands
  // off to the open/closing tween from wherever the finger left it.
  // sheetHalfDetent variants add a middle detent: "half-anim" tweens to the
  // half-size progress and rests at "half" — the card parks over the bottom
  // half of the screen (graph + earth live) while the page keeps scrolling
  // and the HUD date follows the reading position.
  const [expandedPhase, setExpandedPhase] = useState<
    "closed" | "dragging" | "open" | "settled" | "closing" | "half-anim" | "half"
  >("closed")
  // Eased 0..1; 0 = bubble geometry, 1 = dashboard geometry.
  const [morphProgress, setMorphProgress] = useState(0)
  const morphProgressRef = useRef(0)
  // The HUD date is frozen while the overlay is open so the morph graph and
  // the in-flow dashboard graph render the *same* timeline at every frame —
  // per-instance pause-freezing left them on slightly different dates, and
  // the x-axis maps timestamps, so the settle hand-off jumped horizontally.
  const overlayFrozenDateRef = useRef<Date | null>(null)
  // Bumped when the morph settles: the in-flow dashboard graph remounts so
  // its per-instance viewport tweens initialize at-target on the frozen
  // date — exactly like the morph graph did at tap. A long-lived instance
  // carries tween state from its prewarm-era dates, which rendered a
  // different x-mapping than the morph graph's final frame (the ~5px jump).
  const [dashboardGraphEpoch, setDashboardGraphEpoch] = useState(0)
  // Bumped when a close completes, remounting the bubble graph so it
  // initializes at-target — its long-paused instance would otherwise render
  // stale tween state and mismatch the morph's final frame.
  const [bubbleGraphEpoch, setBubbleGraphEpoch] = useState(0)
  const [overlayOriginRect, setOverlayOriginRect] = useState<{
    top: number
    left: number
    width: number
    height: number
  } | null>(null)
  // The dashboard is expensive to mount (globe canvas, treemaps) — mounting
  // it at tap time blocks the main thread and freezes the morph. Instead it
  // mounts hidden as soon as the bubble first shows, so the tap only has to
  // animate already-painted content.
  const [isOverlayPrewarmed, setIsOverlayPrewarmed] = useState(false)
  // Final on-screen rect of the dashboard's graph block (measurable while
  // hidden), used to FLIP it from the bubble's rect.
  const [expandedGraphRect, setExpandedGraphRect] = useState<{
    top: number
    left: number
    width: number
    height: number
  } | null>(null)
  const bubbleContainerRef = useRef<HTMLDivElement | null>(null)
  const expandedGraphBlockRef = useRef<HTMLDivElement | null>(null)
  const overlayScrollerRef = useRef<HTMLDivElement | null>(null)
  const [isBubbleVisible, setIsBubbleVisible] = useState(false)
  // What actually shows/hides the bottom HUD: the scroll trigger above, OR
  // the audio UI being open — pressing the top nav's narration button must
  // fade the whole bottom bar in immediately (the narration controls live in
  // it), even before the reader has scrolled past the top graph. The
  // epilogue overrides both: the bar fades out for the ending and fades
  // back if the reader scrolls up out of it. The overlay prewarm
  // deliberately stays keyed on the scroll trigger alone: prewarming at the
  // top of the page would mount the second globe canvas while the hero
  // globe may still be initializing (see the WebGL-context notes on the
  // prewarm effects); the after-first-paint prewarm covers the audio-open
  // case within a couple of seconds anyway.
  const isHudRevealed =
    (isBubbleVisible || isAudioUiOpen) && !isEpilogueEngaged
  const [viewportWidth, setViewportWidth] = useState<number | null>(null)
  // Content width of the in-flow top-graph block. Measured from the element
  // (not the viewport) so the block inherits the page wrapper's horizontal
  // padding and stays flush with the story text at any width.
  const [topGraphWidth, setTopGraphWidth] = useState<number | null>(null)
  const topGraphContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const updateViewportLayout = () => {
      setViewportWidth(document.documentElement.clientWidth)
      setTopGraphWidth(topGraphContainerRef.current?.clientWidth ?? null)
    }
    updateViewportLayout()
    window.addEventListener("resize", updateViewportLayout)
    return () => window.removeEventListener("resize", updateViewportLayout)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const topGraphElement = topGraphContainerRef.current
      if (!topGraphElement) return
      const nextIsBubbleVisible =
        topGraphElement.getBoundingClientRect().bottom
          < -MOBILE_BUBBLE_FADE_TRIGGER_PX
      setIsBubbleVisible(currentIsBubbleVisible =>
        currentIsBubbleVisible === nextIsBubbleVisible
          ? currentIsBubbleVisible
          : nextIsBubbleVisible
      )
    }
    const visualViewport = window.visualViewport

    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    visualViewport?.addEventListener("scroll", onScroll, { passive: true })
    visualViewport?.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      visualViewport?.removeEventListener("scroll", onScroll)
      visualViewport?.removeEventListener("resize", onScroll)
    }
  }, [])

  useEffect(() => {
    if (
      expandedPhase !== "open"
      && expandedPhase !== "closing"
      && expandedPhase !== "half-anim"
    ) return
    const fromProgress = morphProgressRef.current
    const toProgress = expandedPhase === "open"
      ? 1
      : expandedPhase === "half-anim"
        ? sheetHalfTargetRef.current
        : 0
    // Partial morphs (e.g. closing right after opening) take proportional time.
    const duration = sheetMorphMs * Math.abs(toProgress - fromProgress)
    // Anchored to the first frame's own rAF timestamp and clamped: a
    // performance.now() captured here can postdate the next frame's vsync
    // timestamp (long commit), making t negative and the eased lerp
    // extrapolate — a one-frame giant-graph flash at the start of a close.
    let startedAt: number | null = null
    let frameId: number
    const step = (now: number) => {
      if (startedAt === null) startedAt = now
      const t = duration === 0
        ? 1
        : Math.min(1, Math.max(0, (now - startedAt) / duration))
      const value = lerp(fromProgress, toProgress, easeOutCubic(t))
      morphProgressRef.current = value
      setMorphProgress(value)
      if (t < 1) {
        frameId = requestAnimationFrame(step)
      } else if (expandedPhase === "open") {
        // Fresh-mount the in-flow graph in the same commit that reveals it.
        setDashboardGraphEpoch(epoch => epoch + 1)
        setExpandedPhase("settled")
      } else if (expandedPhase === "half-anim") {
        // Same fresh mount at the half detent — the graph unpauses there
        // and follows the (live) reading date.
        setDashboardGraphEpoch(epoch => epoch + 1)
        setExpandedPhase("half")
      } else {
        // Fresh-mount the bubble graph in the same commit that reveals it.
        setBubbleGraphEpoch(epoch => epoch + 1)
        setExpandedPhase("closed")
      }
    }
    frameId = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frameId)
  }, [expandedPhase, sheetMorphMs])

  useEffect(() => {
    // ?noprewarm disables the automatic overlay prewarms (this one and the
    // idle one below); the tap paths still mount on demand. Kill switch for
    // bisecting device crashes around the bubble's scroll-in — the prewarm
    // is the big allocation spike at that moment (second globe canvas,
    // dashboard graph, panels). Same escape-hatch pattern as ?dither2d.
    if (window.location.search.includes('noprewarm')) return
    // Prewarm (mount the hidden overlay) once the bottom bubble scrolls in.
    // NOT eagerly on mount: the overlay carries its own globe canvas, and
    // mounting a second WebGL globe while the top-graph hero globe is on screen
    // and initializing steals/loses its context (the hero flickers out to a
    // gray box). The top-graph tap path prewarms on demand instead — see
    // openFromTopGraphRef, where the mount is masked by the morph covering the
    // hero. The latch stays true once set.
    //
    // Idle-scheduled, NOT synchronous: this effect fires from the scroll
    // handler, and committing the whole overlay (globe canvas + graph +
    // panels) in that same commit put a multi-hundred-ms task in the middle
    // of an active scroll — exactly when the strip is also painting its
    // first frames. Same scheduling as the after-first-paint prewarm below;
    // the timeout bounds how long a busy main thread can defer it.
    if (!isBubbleVisible || isOverlayPrewarmed) return
    let idleId: number | undefined
    let timerId: number | undefined
    const prewarm = () => setIsOverlayPrewarmed(true)
    if (window.requestIdleCallback) {
      idleId = window.requestIdleCallback(prewarm, { timeout: 1500 })
    } else {
      // No requestIdleCallback (older Safari): a short timeout still moves
      // the mount off the scroll-triggered commit.
      timerId = window.setTimeout(prewarm, 300)
    }
    return () => {
      if (idleId !== undefined) window.cancelIdleCallback?.(idleId)
      if (timerId !== undefined) window.clearTimeout(timerId)
    }
  }, [isBubbleVisible, isOverlayPrewarmed])

  useEffect(() => {
    // ?noprewarm — see the scroll-in prewarm above.
    if (window.location.search.includes('noprewarm')) return
    // Proactively prewarm a couple seconds after first paint, so the FIRST
    // tap-to-open only animates instead of synchronously mounting the overlay
    // (a second globe + the dashboard graph + all the panels) during the morph
    // — that cold mount is ~half a second of dropped frames on a phone. Two
    // guards make this safe where an eager on-mount prewarm was not: (1) the
    // delay lets the hero top-graph globe finish creating its WebGL context
    // first — mounting the second globe concurrently with that blanks the hero
    // to a gray box; (2) requestIdleCallback slots the heavy mount into idle
    // time so it doesn't itself jank a frame. If the user taps before this
    // fires, openFromTopGraphRef still prewarms on demand (the old behavior).
    if (isOverlayPrewarmed || viewportWidth === null) return
    let idleId: number | undefined
    const timer = window.setTimeout(() => {
      const ric = window.requestIdleCallback
      if (ric) {
        idleId = ric(() => setIsOverlayPrewarmed(true), { timeout: 1000 })
      } else {
        setIsOverlayPrewarmed(true)
      }
    }, 2000)
    return () => {
      window.clearTimeout(timer)
      if (idleId !== undefined) window.cancelIdleCallback?.(idleId)
    }
  }, [isOverlayPrewarmed, viewportWidth])

  useEffect(() => {
    if (!isOverlayPrewarmed) return
    const measureExpandedGraphRect = () => {
      const graphRect = expandedGraphBlockRef.current?.getBoundingClientRect()
      if (!graphRect) return
      setExpandedGraphRect({
        top: graphRect.top,
        left: graphRect.left,
        width: graphRect.width,
        height: graphRect.height,
      })
    }
    // After paint, once the hidden overlay has laid out.
    const frameId = requestAnimationFrame(measureExpandedGraphRect)
    window.addEventListener("resize", measureExpandedGraphRect)
    return () => {
      cancelAnimationFrame(frameId)
      window.removeEventListener("resize", measureExpandedGraphRect)
    }
  }, [isOverlayPrewarmed, viewportWidth])

  const measureBubbleRect = () => {
    const bubbleRect = bubbleContainerRef.current?.getBoundingClientRect()
    if (!bubbleRect) return null
    return {
      top: bubbleRect.top,
      left: bubbleRect.left,
      width: bubbleRect.width,
      height: bubbleRect.height,
    }
  }

  useEffect(() => {
    // "half" deliberately does NOT lock: the card parks over the bottom half
    // and the whole point is that the page keeps scrolling behind it.
    if (expandedPhase === "closed" || expandedPhase === "half") return

    const root = document.documentElement
    const body = document.body
    const previousRootOverflowY = root.style.overflowY
    const previousBodyOverflowY = body.style.overflowY
    root.style.overflowY = "hidden"
    body.style.overflowY = "hidden"

    return () => {
      root.style.overflowY = previousRootOverflowY
      body.style.overflowY = previousBodyOverflowY
    }
  }, [expandedPhase])

  useEffect(() => {
    const root = document.documentElement
    const body = document.body
    const mobileViewportQuery = window.matchMedia("(max-width: 767px)")
    const previousRootOverflowX = root.style.overflowX
    const previousBodyOverflowX = body.style.overflowX
    const updateMobileOverflow = () => {
      root.style.overflowX = mobileViewportQuery.matches
        ? "hidden"
        : previousRootOverflowX
      body.style.overflowX = mobileViewportQuery.matches
        ? "hidden"
        : previousBodyOverflowX
    }

    updateMobileOverflow()
    mobileViewportQuery.addEventListener("change", updateMobileOverflow)
    return () => {
      mobileViewportQuery.removeEventListener("change", updateMobileOverflow)
      root.style.overflowX = previousRootOverflowX
      body.style.overflowX = previousBodyOverflowX
    }
  }, [])

  // The expanded dashboard fills the full screen width (no max-width cap), less
  // a small horizontal padding so content isn't flush to the edges. The 32 is
  // 16px each side — keep it matched to the content wrapper's px-4 below.
  // Sheet mode lays out inside the floating card instead, which keeps the
  // strip's side margins.
  const expandedContentWidth = viewportWidth !== null
    ? Math.max(
        0,
        (isSheetMode ? viewportWidth - previewMarginPx * 2 : viewportWidth) - 32,
      )
    : null
  const currentYear = props.currentDate.getFullYear()
  const bubbleWidth = viewportWidth !== null
    ? viewportWidth - previewMarginPx * 2
    : null
  const bubbleGraphRenderWidth = bubbleWidth !== null
    ? bubbleWidth + MOBILE_GRAPH_BLEED_PX * 2
    : null
  const topGraphScale = topGraphWidth !== null
    ? topGraphWidth / DESKTOP_TOP_GRAPH_WIDTH_PX
    : null
  const topGraphBlockHeight = topGraphScale !== null
    ? PRIMARY_GRAPH_HEIGHT_PX * topGraphScale
    : null

  // Frozen except at a resting detent (full or half) — the settle remount
  // (dashboardGraphEpoch) gives it fresh at-target state, so it never needs
  // to run hidden. At "half" it runs LIVE, following the reading date.
  const isExpandedGraphPaused =
    expandedPhase !== "settled" && expandedPhase !== "half"
  // The globe stays paused until a resting detent: it fades in fresh, and
  // keeping its canvas loop off through the morph saves main-thread budget.
  const isExpandedGlobePaused =
    expandedPhase !== "settled" && expandedPhase !== "half"
  // Heavy subtrees, memoized so the morph's phase flips re-render only the
  // animated wrapper styles (a full subtree re-render at tap time delayed
  // the first animation frame by hundreds of ms). Data flows into these via
  // HudContext, so they still update with the story.
  const topGraphContent = useMemo(
    () => <HudPersistentGlobeAndGraph />,
    [],
  )
  const expandedGraphContent = useMemo(
    () => (
      <div className="relative">
        {expandedContentWidth !== null && (
          // The channel + bridge mirror desktop's HudPersistentGlobeAndGraph
          // wiring: the graph publishes its point-on-line hover payload into
          // the channel, and the bridge forwards it to the capability hover
          // swap, so touching the capability line swaps the panel stack below
          // for the capability ladder — same as hovering it on desktop.
          <BandHudOverlayProvider>
            <MobileCapabilitiesGraph
              key={dashboardGraphEpoch}
              desktopRendering
              width={expandedContentWidth}
              height={MOBILE_EXPANDED_GRAPH_HEIGHT_PX}
              showLabels
              revealAnnotations
              showRiskBackgroundLabels
              // Frozen except when fully open: unpausing re-renders the
              // whole graph (date catch-up), which would stall the morph.
              paused={isExpandedGraphPaused}
            />
            <CapabilityHoverBridge />
          </BandHudOverlayProvider>
        )}
      </div>
    ),
    [expandedContentWidth, isExpandedGraphPaused, dashboardGraphEpoch],
  )
  // Separate from the graph content: the globe rides the FLIPping container
  // but hides during the morph — the non-uniform squash turns a sphere into
  // an ellipse — fading in only once the container is nearly unsquashed.
  const expandedGlobeContent = useMemo(
    () => (
      <HudGlobeBlock
        className="absolute -left-[34px] -top-[12px] z-[5] pointer-events-none"
        size={MOBILE_EXPANDED_GLOBE_SIZE_PX}
        lockSizeToInitialHeight={false}
        minDevicePixelRatio={MOBILE_GLOBE_MIN_DEVICE_PIXEL_RATIO}
        // Mounted (prewarmed) long before it's shown; don't let the
        // hidden canvas render at 60fps, nor mid-morph.
        paused={isExpandedGlobePaused}
      />
    ),
    [isExpandedGlobePaused],
  )
  // The expanded dashboard fills the viewport width (minus px-4 padding).
  // The 4-across stat row needs every pixel it can get beside the
  // power-distribution diagram, so the mobile dashboard ALWAYS runs the
  // compact treatment — compact stat fonts/gaps and the narrowed power
  // column below. (It was gated on viewport < 420, which missed 428-430px
  // phones and clipped "Total Slowdown" there; this component is
  // mobile-only, so there is nothing to gate.)
  const statsCompact = true
  // The compact 4-across stat row bottoms out around 160px (its labels'
  // min-content widths plus minimum gaps), and the panel row's column gap is
  // 20px — below ~300px of content width a fixed 120px power column would
  // push the stats underneath the power diagram. Give the power column only
  // what's left after reserving that much for the stats, capped at the
  // design's 120 (holds until ~350px viewports) and floored so the diagram
  // stays legible on the very narrowest screens.
  const powerColumnWidthPx = expandedContentWidth !== null
    ? Math.max(72, Math.min(120, expandedContentWidth - 180))
    : 120
  const dashboardPanelsContent = useMemo(
    () => (
      // Desktop HUD variant 0's actual panel stack (policies, stats, power
      // treemap, labor strip) with its full hover-swap/metric-detail wiring —
      // the same component the desktop variant renders, at mobile widths.
      // Must render inside BelowDetailHudSwapProviders (see the overlay).
      <BelowDetailHudPanels
        opts={BELOW_DETAIL_HUD_BODY_OPTS}
        powerColumnWidthPx={powerColumnWidthPx}
        statsCompact={statsCompact}
        galaxyHideForceVisible
        // Phone columns can't fit both labor-legend entries on one line
        // since the "at up to ×N speed" copy — stacked rows keep each
        // speed leader-aligned to the column's right edge.
        laborLegendLayout="rows"
      />
    ),
    [statsCompact, powerColumnWidthPx],
  )

  // Tap-open shared by the plain strip button, the sheet gestures' tap
  // fallbacks, and hold-to-lift's quick tap. Half-detent sheets tap-open to
  // HALF (the gentle first stop); everything else goes straight to full.
  const openSheetFromTap = () => {
    const originRect = measureBubbleRect()
    setOverlayOriginRect(originRect)
    if (overlayScrollerRef.current) overlayScrollerRef.current.scrollTop = 0
    overlayFrozenDateRef.current = props.currentDate
    morphProgressRef.current = 0
    setMorphProgress(0)
    if (isSheetMode && sheetHalfDetent !== null && originRect !== null) {
      sheetHalfTargetRef.current = sheetHalfDetentProgress(
        originRect.top,
        sheetHalfDetent.viewportFraction,
      )
      setExpandedPhase("half-anim")
    } else {
      setExpandedPhase("open")
    }
  }

  const handlePreviewClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (didPreviewPointerMoveRef.current) {
      event.preventDefault()
      didPreviewPointerMoveRef.current = false
      return
    }
    openSheetFromTap()
  }

  // Finger-driven sheet drag from the grabber bar (expandHandle.draggable
  // variants) — or from anywhere on the strip (stripDragAnywhere). Past a
  // small upward threshold the overlay enters "dragging" and morphProgress
  // tracks the finger directly — progress = upward travel over the
  // card-top's distance to the viewport top, so the sheet's top edge
  // roughly rides the finger. Release hands off to the existing tween:
  // forward past the snap point (or on an upward flick), back otherwise.
  // Listeners live on window so the hotspot unmounting can't drop the
  // gesture mid-drag; a release that never crossed the threshold NOR moved
  // meaningfully in any direction is a plain tap and opens like one (a
  // sideways/downward swipe is neither a lift nor a tap — it's dropped).
  const beginSheetDrag = (startClientX: number, startClientY: number) => {
    if (expandedPhase !== "closed") return
    const frozenDate = props.currentDate
    let engaged = false
    let rangePx = 1
    let lastY = startClientY
    let lastMoveAt = performance.now()
    let velocityUpPxPerMs = 0
    let progress = 0
    let maxTravelPx = 0

    const onMove = (moveEvent: PointerEvent) => {
      const now = performance.now()
      velocityUpPxPerMs = (lastY - moveEvent.clientY) / Math.max(1, now - lastMoveAt)
      lastY = moveEvent.clientY
      lastMoveAt = now
      maxTravelPx = Math.max(
        maxTravelPx,
        Math.hypot(
          moveEvent.clientX - startClientX,
          moveEvent.clientY - startClientY,
        ),
      )
      const upwardPx = startClientY - moveEvent.clientY
      if (!engaged) {
        if (upwardPx <= SHEET_DRAG_START_THRESHOLD_PX) return
        const originRect = measureBubbleRect()
        if (!originRect) return
        engaged = true
        rangePx = Math.max(200, originRect.top)
        if (sheetHalfDetent !== null) {
          sheetHalfTargetRef.current = sheetHalfDetentProgress(
            originRect.top,
            sheetHalfDetent.viewportFraction,
          )
        }
        setOverlayOriginRect(originRect)
        if (overlayScrollerRef.current) overlayScrollerRef.current.scrollTop = 0
        overlayFrozenDateRef.current = frozenDate
        setExpandedPhase("dragging")
      }
      progress = Math.min(1, Math.max(0,
        (upwardPx - SHEET_DRAG_START_THRESHOLD_PX) / rangePx))
      morphProgressRef.current = progress
      setMorphProgress(progress)
    }
    const onEnd = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onEnd)
      window.removeEventListener("pointercancel", onEnd)
      if (!engaged) {
        // A swipe that went somewhere other than up: not a lift, not a tap.
        if (maxTravelPx > MOBILE_PREVIEW_TAP_CANCEL_DISTANCE_PX) return
        // Never armed, never moved: a tap — open like the strip tap
        // (half-detent variants land at half; see openSheetFromTap).
        openSheetFromTap()
        return
      }
      if (sheetHalfDetent !== null) {
        setExpandedPhase(chooseSheetDetentPhase(progress, velocityUpPxPerMs))
        return
      }
      const shouldOpen = progress > SHEET_DRAG_OPEN_PROGRESS
        || velocityUpPxPerMs > SHEET_DRAG_FLICK_VELOCITY_PX_PER_MS
      setExpandedPhase(shouldOpen ? "open" : "closing")
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onEnd)
    window.addEventListener("pointercancel", onEnd)
  }

  // Hold-to-lift strip surface (stripHoldToLiftMs variants). The strip keeps
  // touch-action: pan-y, so a swipe that starts moving right away is claimed
  // by the browser as a normal page scroll. Resting the finger for the hold
  // delay ARMS the lift: the live gesture is handed to the shared sheet-drag
  // session (beginSheetDrag), and a window-level non-passive touchmove
  // listener preventDefaults armed moves so the page doesn't also scroll —
  // arming requires stillness, so the browser hasn't claimed the pan yet.
  // A quick still tap (released before the hold delay) opens as usual.
  const holdToLiftArmedRef = useRef(false)
  const beginHoldToLift = (startClientX: number, startClientY: number) => {
    if (expandedPhase !== "closed") return
    const holdMs = stripConfig?.stripHoldToLiftMs ?? 150
    let lastX = startClientX
    let lastY = startClientY
    let cancelled = false
    const onTouchMove = (touchEvent: TouchEvent) => {
      if (holdToLiftArmedRef.current && touchEvent.cancelable) {
        touchEvent.preventDefault()
      }
    }
    const removePreArmListeners = () => {
      window.clearTimeout(holdTimer)
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onCancel)
    }
    const onMove = (moveEvent: PointerEvent) => {
      lastX = moveEvent.clientX
      lastY = moveEvent.clientY
      const travelPx = Math.hypot(
        moveEvent.clientX - startClientX,
        moveEvent.clientY - startClientY,
      )
      // Moved before the hold elapsed: a reading swipe — the native pan-y
      // scroll owns it, we stand down entirely.
      if (travelPx > MOBILE_PREVIEW_TAP_CANCEL_DISTANCE_PX) {
        cancelled = true
        removePreArmListeners()
        window.removeEventListener("touchmove", onTouchMove)
      }
    }
    const onUp = () => {
      removePreArmListeners()
      window.removeEventListener("touchmove", onTouchMove)
      if (!cancelled) openSheetFromTap()
    }
    const onCancel = () => {
      removePreArmListeners()
      window.removeEventListener("touchmove", onTouchMove)
    }
    const holdTimer = window.setTimeout(() => {
      holdToLiftArmedRef.current = true
      removePreArmListeners()
      // Disarm (and stop eating touchmoves) when the finger lifts; the
      // sheet-drag session has its own window listeners for the gesture.
      const disarm = () => {
        holdToLiftArmedRef.current = false
        window.removeEventListener("touchmove", onTouchMove)
        window.removeEventListener("pointerup", disarm)
        window.removeEventListener("pointercancel", disarm)
      }
      window.addEventListener("pointerup", disarm)
      window.addEventListener("pointercancel", disarm)
      beginSheetDrag(lastX, lastY)
    }, holdMs)
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onCancel)
    window.addEventListener("touchmove", onTouchMove, { passive: false })
  }

  // Shared by the ✕ button, the sheet scrim tap, and the card pill tap.
  const closeExpandedOverlay = () => {
    setOverlayOriginRect(measureBubbleRect())
    // The dashboard may be scrolled; the shrink must start from the
    // graph block's current on-screen position.
    const blockRect = expandedGraphBlockRef.current?.getBoundingClientRect()
    if (blockRect) {
      setExpandedGraphRect({
        top: blockRect.top,
        left: blockRect.left,
        width: blockRect.width,
        height: blockRect.height,
      })
    }
    setExpandedPhase("closing")
  }

  // The epilogue hides the collapsed card via isHudRevealed, but the
  // expanded overlay is its own fixed layer — if the reader reaches the
  // epilogue with the dashboard open (e.g. scrolling behind a half-detent
  // card), close it rather than strand a dashboard over the ending.
  useEffect(() => {
    if (!isEpilogueEngaged) return
    if (expandedPhase === "closed" || expandedPhase === "closing") return
    closeExpandedOverlay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEpilogueEngaged, expandedPhase])

  // The reverse gesture: from a resting card (settled at full, or parked at
  // the half detent), the top pill / dismiss zone drags the sheet. From full
  // it runs DOWN; from the half detent it runs BOTH ways — down toward
  // closed, up toward full. Release hands off to the tween: half-detent
  // variants snap to the nearest detent (chooseSheetDetentPhase), plain
  // sheets close or spring back open. A tap that never crosses the
  // threshold closes outright — the pill replaces the ✕.
  // closeOnTap false = drag-only (the generous sheetDismissZone over the
  // graph: a stray tap there must not close the card).
  const beginSheetDismissDrag = (startClientY: number, closeOnTap = true) => {
    if (expandedPhase !== "settled" && expandedPhase !== "half") return
    const startedAtHalf = expandedPhase === "half"
    const originRect = measureBubbleRect()
    if (!originRect) return
    const rangePx = Math.max(200, originRect.top - SHEET_CARD_TOP_GAP_PX)
    const startProgress = morphProgressRef.current
    // Leaving the half detent the HUD date freezes again — pin it to the
    // current reading position so a pull to full doesn't snap the graph
    // back to whenever the sheet was first opened.
    if (startedAtHalf) overlayFrozenDateRef.current = props.currentDate
    if (sheetHalfDetent !== null) {
      sheetHalfTargetRef.current = sheetHalfDetentProgress(
        originRect.top,
        sheetHalfDetent.viewportFraction,
      )
    }
    let engaged = false
    let lastY = startClientY
    let lastMoveAt = performance.now()
    let velocityDownPxPerMs = 0
    let progress = startProgress

    const onMove = (moveEvent: PointerEvent) => {
      const now = performance.now()
      velocityDownPxPerMs = (moveEvent.clientY - lastY) / Math.max(1, now - lastMoveAt)
      lastY = moveEvent.clientY
      lastMoveAt = now
      const downwardPx = moveEvent.clientY - startClientY
      if (!engaged) {
        // From full only a downward pull arms; from the half detent the
        // gesture arms in either direction (up grows, down dismisses).
        if (startedAtHalf
          ? Math.abs(downwardPx) <= SHEET_DRAG_START_THRESHOLD_PX
          : downwardPx <= SHEET_DRAG_START_THRESHOLD_PX) return
        engaged = true
        setOverlayOriginRect(originRect)
        setExpandedPhase("dragging")
      }
      const travelPx = downwardPx > 0
        ? downwardPx - SHEET_DRAG_START_THRESHOLD_PX
        : downwardPx + SHEET_DRAG_START_THRESHOLD_PX
      progress = Math.min(1, Math.max(0, startProgress - travelPx / rangePx))
      morphProgressRef.current = progress
      setMorphProgress(progress)
    }
    const onEnd = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onEnd)
      window.removeEventListener("pointercancel", onEnd)
      if (!engaged) {
        // Never armed: a tap. On the pill it closes; on the drag-only
        // swipe zone it's a no-op.
        if (closeOnTap) closeExpandedOverlay()
        return
      }
      if (sheetHalfDetent !== null) {
        setExpandedPhase(chooseSheetDetentPhase(progress, -velocityDownPxPerMs))
        return
      }
      const shouldClose = progress < SHEET_DISMISS_CLOSE_PROGRESS
        || velocityDownPxPerMs > SHEET_DRAG_FLICK_VELOCITY_PX_PER_MS
      setExpandedPhase(shouldClose ? "closing" : "open")
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onEnd)
    window.addEventListener("pointercancel", onEnd)
  }

  // Tapping the planet in the top graph is INERT (2026-07-09, by request —
  // it used to morph the dashboard open from the hero's rect). useCameraDrag
  // still fires its bubbling "globetap" on the canvas; nothing listens. The
  // globe keeps rotating on drag.

  const hudDate =
    expandedPhase !== "closed"
      && expandedPhase !== "half"
      && overlayFrozenDateRef.current !== null
      ? overlayFrozenDateRef.current
      : props.currentDate

  // Dashboard content fades in over a slice of the morph tween (progress
  // start->end), so the graph/globe/panels come in *while* the overlay
  // is opening rather than popping at the end. Driven by morphProgress (the
  // same value that opens the clip), so the reveal stays in lockstep with the
  // geometry, and reverses cleanly on close (progress 1->0).
  const revealOpacity = (start: number, end: number) =>
    Math.min(1, Math.max(0, (morphProgress - start) / (end - start)))

  // The opening clip window (bubble rect -> full screen). Guarded for SSR
  // (window); only evaluated meaningfully once the overlay is
  // client-prewarmed with an origin rect.
  const overlayClipPath =
    overlayOriginRect === null || typeof window === "undefined"
      ? "inset(0px 0px 0px 0px round 0px)"
      : isSheetMode
      // Sheet mode: only the TOP edge travels — the pane keeps the strip's
      // side margins, bottom gap, and corner radius the whole way, and the
      // top stops short of the viewport top, so the full state reads as a
      // floating overlay card over the page.
      ? `inset(${
          lerp(overlayOriginRect.top, SHEET_CARD_TOP_GAP_PX, morphProgress)
        }px ${
          Math.max(0, viewportWidth! - overlayOriginRect.left - overlayOriginRect.width)
        }px ${
          Math.max(0, window.innerHeight - overlayOriginRect.top - overlayOriginRect.height)
        }px ${overlayOriginRect.left}px round ${MOBILE_BUBBLE_RADIUS_PX}px)`
      : `inset(${lerp(overlayOriginRect.top, 0, morphProgress)}px ${
          lerp(
            Math.max(0, viewportWidth! - overlayOriginRect.left - overlayOriginRect.width),
            0,
            morphProgress,
          )
        }px ${
          lerp(
            Math.max(0, window.innerHeight - overlayOriginRect.top - overlayOriginRect.height),
            0,
            morphProgress,
          )
        }px ${lerp(overlayOriginRect.left, 0, morphProgress)}px round ${
          lerp(MOBILE_BUBBLE_RADIUS_PX, 0, morphProgress)
        }px)`

  // The fixed card's inner content — chart/strip + date, crossfading with
  // the dashboard during the morph. Shared by both chrome treatments
  // (paper card / flush strip) below.
  const previewInner = bubbleWidth === null ? null : (
    // Top-anchored at the graph's own height (not inset-0): when the audio
    // row is open the card is taller, and the graph keeps its geometry at the
    // top while the row takes the bottom strip.
    <div
      className="absolute inset-x-0 top-0"
      style={{
        height: previewHeightPx,
        opacity:
          expandedPhase === "closed" ? 1 : 1 - revealOpacity(0, 0.4),
      }}
    >
      {design !== "bubble" ? (
        <MobileCompactHudStrip
          key={bubbleGraphEpoch}
          variant={design}
          dateIsHoverAutoplay={isDateHoverAutoplay}
          width={bubbleWidth}
          height={previewHeightPx}
          currentDate={bubbleChartDate}
          points={capabilityPoints}
          textColor={props.pageTextColor}
          backgroundColor={props.pageBackgroundColor}
          globePaused={!isHudRevealed || expandedPhase !== "closed"}
        />
      ) : (
        <>
          <div className="absolute inset-x-0 top-0">
            {/* AI-2027-style self-contained chart: all geometry is derived
                from the raw date and every element is animated by
                framer-motion on one 0.5s curve — no hand-rolled viewport
                tweens, so the motion stays coordinated and smooth. */}
            <MobileCapabilityLineChart
              key={bubbleGraphEpoch}
              currentDate={bubbleChartDate}
              points={capabilityPoints}
              width={bubbleWidth}
              height={MOBILE_PREVIEW_GRAPH_HEIGHT_PX}
              strokeWidth={GRAPH_LINE_STROKE_WIDTH * MOBILE_BUBBLE_LINE_STROKE_SCALE}
              lineColor={props.pageTextColor}
              textColor={props.pageTextColor}
              backgroundColor={props.pageBackgroundColor}
              showLabelOutline
            />
          </div>
          <div
            className="absolute bottom-2 right-2 rounded-sm px-2.5 py-1.5 font-mono text-xs font-bold leading-none"
            style={{
              backgroundColor: props.pageTextColor,
              color: props.pageBackgroundColor,
            }}
          >
            {currentYear}
          </div>
        </>
      )}
    </div>
  )

  return (
    <HudContextProvider {...props} currentDate={hudDate}>
      {/* Always mounted (even before measurement) so the width effect can
          read this block's own content width — it spans the page container
          like the story text, keeping their edges homogeneous. */}
      <div
        ref={topGraphContainerRef}
        // The top graph is display-only on mobile: make the entire graph layer
        // pointer-inert — not just its hover wrapper but the SVG line/paths too,
        // which otherwise sit on top of the globe and swallow touches over the
        // upper half of the planet (so only the bare centre was grabbable). The
        // globe `<canvas>` is the one exemption: it re-enables itself via inline
        // `pointer-events` (in useCameraDrag), which beats this rule, so it still
        // rotates on drag and opens the dashboard on tap; everything else lets
        // touches pass through to page scroll and nothing hovers.
        className="md:hidden relative w-full mb-6 [&_*:not(canvas)]:!pointer-events-none"
        style={{ height: topGraphBlockHeight ?? undefined }}
      >
        {topGraphScale !== null && (
          <div
            className="absolute left-0 top-0"
            style={{
              width: DESKTOP_TOP_GRAPH_WIDTH_PX,
              transform: `scale(${topGraphScale})`,
              transformOrigin: "top left",
            }}
          >
            {topGraphContent}
          </div>
        )}
      </div>
      {viewportWidth !== null
        && bubbleWidth !== null
        && bubbleGraphRenderWidth !== null && (
        <div
          ref={bubbleContainerRef}
          className={[
            "md:hidden fixed z-[40] block",
            // The flush strip lets its oversized globe poke above the top
            // edge, and globeOverflowsCard variants let their escaped globe
            // overlap the page; every other chrome clips to the rounded card
            // (globeOverflowsCard strips re-clip their graph internally).
            stripConfig?.chrome === "flush" || stripConfig?.globeOverflowsCard
              ? ""
              : "overflow-hidden",
            "pointer-events-none font-mono text-[12px]",
          ].join(" ")}
          style={{
            left: previewMarginPx,
            bottom: `calc(${previewMarginPx}px + env(safe-area-inset-bottom))`,
            width: bubbleWidth,
            height: previewHeightPx + audioBarHeightPx,
            color: props.pageTextColor,
            // borderRadius clips the graph's horizontal bleed to the rounded
            // rect; the paper card material is the child div below.
            borderRadius: previewRadiusPx,
            // The card is the same paper as the page, so without a border
            // its lower boundary needs a cue — a soft diffuse shadow lifts
            // the sheet off the scrolling text without reintroducing chrome.
            // (box-shadow isn't clipped by the element's own overflow.)
            boxShadow: stripConfig?.chrome === "flush"
              ? undefined
              : "0 2px 14px rgba(0, 0, 0, 0.18), 0 1px 3px rgba(0, 0, 0, 0.10)",
            // Closed: opacity follows the scroll-in trigger (300ms). During
            // the morph the card stays fully painted (only the chart content
            // inside fades — see previewInner); it's covered by the overlay
            // throughout, so full opacity costs nothing visually. The 300ms
            // transition is only for the reveal (isHudRevealed false->true:
            // scrolling past the top graph, or opening the audio UI from the
            // top nav). Height animates the audio row's reveal: the bottom
            // edge is fixed, so the card's top slides up and the graph lifts
            // to uncover the row.
            opacity: isHudRevealed ? 1 : 0,
            transition: `opacity ${MOBILE_HUD_TRANSITION_MS}ms ease-out, height ${MOBILE_HUD_TRANSITION_MS}ms ease-out`,
            visibility: isHudRevealed ? "visible" : "hidden",
          }}
        >
          {/* Flat paper chrome: a borderless opaque page-background card
              (the dither field and globe give it its edges; the soft shadow
              on the container above is the boundary cue — the parallel
              1px-outline pass was superseded by it). "flush" adds only a
              black top rule for the edge-to-edge strip. The chart + year
              pill inside crossfade with the dashboard during the morph —
              fading out over the first 40% of the open and back in over the
              last 40% of the close — while the card itself stays fully
              painted. */}
          <div
            className="absolute inset-0"
            style={{
              backgroundColor: props.pageBackgroundColor,
              borderRadius: previewRadiusPx,
              ...(stripConfig?.chrome === "flush"
                ? { borderTop: `1px solid ${props.pageTextColor}` }
                : {}),
            }}
          >
            {previewInner}
            {/* Narration row under the graph. pointerEvents flips off during
                the morph (the fixed card sits under the rising overlay, and a
                fading row must not eat touches); opacity crossfades with the
                strip content above. */}
            {isAudioUiOpen && (
              <div
                className="absolute inset-x-0 bottom-0"
                style={{
                  height: MOBILE_HUD_AUDIO_BAR_HEIGHT_PX,
                  opacity:
                    expandedPhase === "closed" ? 1 : 1 - revealOpacity(0, 0.4),
                  pointerEvents: expandedPhase === "closed" ? "auto" : "none",
                }}
              >
                <MobileHudAudioBar
                  textColor={props.pageTextColor}
                  backgroundColor={props.pageBackgroundColor}
                />
              </div>
            )}
          </div>
          {/* globeOverflowsCard: the earth escapes the card — the fixed
              container clips to the rounded rect (overflow-hidden), so the
              globe rides OUTSIDE it as a sibling, overlapping the page above
              the strip. Same crossfade as previewInner during the morph. */}
          {design !== "bubble"
            && COMPACT_STRIP_VARIANTS[design].globeOverflowsCard && (
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                // The escaped earth pokes ABOVE the strip, so the rising pane
                // doesn't cover it at low progress — in sheet mode it gets
                // out of the way fast instead of lingering over the pane.
                opacity: expandedPhase === "closed"
                  ? 1
                  : 1 - revealOpacity(0, isSheetMode ? 0.15 : 0.4),
              }}
            >
              <MobileStripGlobeLayer
                variant={design}
                height={previewHeightPx}
                globePaused={!isHudRevealed || expandedPhase !== "closed"}
              />
            </div>
          )}
        </div>
      )}
      {/* stripDragAnywhere: the WHOLE strip is the lift-up surface — any
          pointer-down starts the sheet drag (a still tap opens, an upward
          pull rides the finger; the gesture owns the touch, so no pan-y).
          Mounted through "dragging" like the pill hotspot, so the browser
          keeps honoring touch-action: none for the in-flight gesture. */}
      {isHudRevealed
        && bubbleWidth !== null
        && stripConfig?.stripDragAnywhere
        && sheetDragHandle !== null
        && (expandedPhase === "closed" || expandedPhase === "dragging") && (
        <button
          type="button"
          className={[
            "md:hidden fixed z-[41] block",
            "cursor-grab border-0 bg-transparent p-0",
          ].join(" ")}
          style={{
            left: previewMarginPx,
            bottom: `calc(${previewMarginPx + audioBarHeightPx}px + env(safe-area-inset-bottom))`,
            width: bubbleWidth,
            height: previewHeightPx,
            borderRadius: previewRadiusPx,
            touchAction: "none",
          }}
          aria-label="Drag up or tap to expand data dashboard"
          aria-expanded={false}
          onPointerDown={event => {
            event.preventDefault()
            beginSheetDrag(event.clientX, event.clientY)
          }}
        />
      )}
      {/* Hold-to-lift strip surface: pan-y keeps reading swipes native; the
          hold-then-pull gesture (beginHoldToLift) grabs the sheet. Mounted
          through "dragging" so the armed gesture isn't dropped mid-flight. */}
      {isHudRevealed
        && bubbleWidth !== null
        && stripConfig?.stripHoldToLiftMs != null
        && sheetDragHandle !== null
        && (expandedPhase === "closed" || expandedPhase === "dragging") && (
        <button
          type="button"
          className={[
            "md:hidden fixed z-[41] block",
            "cursor-grab border-0 bg-transparent p-0",
          ].join(" ")}
          style={{
            left: previewMarginPx,
            bottom: `calc(${previewMarginPx + audioBarHeightPx}px + env(safe-area-inset-bottom))`,
            width: bubbleWidth,
            height: previewHeightPx,
            borderRadius: previewRadiusPx,
            touchAction: "pan-y",
          }}
          aria-label="Hold and drag up, or tap, to expand data dashboard"
          aria-expanded={false}
          onPointerDown={event => {
            beginHoldToLift(event.clientX, event.clientY)
          }}
        />
      )}
      {isHudRevealed
        && expandedPhase === "closed"
        && bubbleWidth !== null
        && !stripConfig?.stripDragAnywhere
        && stripConfig?.stripHoldToLiftMs == null && (
        <button
          type="button"
          className={[
            "md:hidden fixed z-[41] block",
            "cursor-pointer border-0 bg-transparent p-0",
          ].join(" ")}
          style={{
            left: previewMarginPx,
            bottom: `calc(${previewMarginPx + audioBarHeightPx}px + env(safe-area-inset-bottom))`,
            width: bubbleWidth,
            height: previewHeightPx,
            borderRadius: previewRadiusPx,
            touchAction: "pan-y",
          }}
          aria-label="Expand data dashboard"
          aria-expanded={false}
          onClick={handlePreviewClick}
          onPointerDown={event => {
            previewPointerStartRef.current = {
              x: event.clientX,
              y: event.clientY,
            }
            didPreviewPointerMoveRef.current = false
          }}
          onPointerMove={event => {
            const pointerStart = previewPointerStartRef.current
            if (!pointerStart) return
            const pointerMovement = Math.hypot(
              event.clientX - pointerStart.x,
              event.clientY - pointerStart.y
            )
            if (pointerMovement > MOBILE_PREVIEW_TAP_CANCEL_DISTANCE_PX) {
              didPreviewPointerMoveRef.current = true
            }
          }}
          onPointerCancel={() => {
            previewPointerStartRef.current = null
            didPreviewPointerMoveRef.current = true
          }}
          onPointerUp={() => {
            previewPointerStartRef.current = null
          }}
        />
      )}
      {/* Sheet-drag hotspot over the grabber bar. Stays mounted through
          "dragging" so the browser keeps honoring its touch-action: none for
          the in-flight gesture (the move/up listeners are on window). */}
      {isHudRevealed
        && (expandedPhase === "closed" || expandedPhase === "dragging")
        && bubbleWidth !== null
        && sheetDragHandle !== null && (
        <button
          type="button"
          aria-label="Drag to expand data dashboard"
          className="md:hidden fixed z-[42] block cursor-grab border-0 bg-transparent p-0"
          style={{
            left: previewMarginPx
              + bubbleWidth / 2
              - (sheetDragHandle.widthPx + SHEET_DRAG_HOTSPOT_WIDTH_EXTRA_PX) / 2,
            bottom: `calc(${
              previewMarginPx
                + audioBarHeightPx
                + previewHeightPx
                - SHEET_DRAG_HOTSPOT_HEIGHT_PX
            }px + env(safe-area-inset-bottom))`,
            width: sheetDragHandle.widthPx + SHEET_DRAG_HOTSPOT_WIDTH_EXTRA_PX,
            height: SHEET_DRAG_HOTSPOT_HEIGHT_PX,
            touchAction: "none",
          }}
          onPointerDown={event => {
            event.preventDefault()
            beginSheetDrag(event.clientX, event.clientY)
          }}
        />
      )}
      {/* Sheet mode: dim scrim behind the floating card — fades in as the
          pane approaches full open; tapping the page around the card closes
          it (interactive only once settled, so it can't eat the drag). */}
      {isSheetMode && isOverlayPrewarmed && expandedPhase !== "closed" && (
        <div
          aria-hidden
          className="md:hidden fixed inset-0 z-[58] bg-black"
          style={{
            // The half detent is a reading state — never dim the page there
            // (on short screens the half progress can cross the 0.7 ramp).
            opacity: expandedPhase === "half"
              ? 0
              : SHEET_CARD_SCRIM_OPACITY * revealOpacity(0.7, 1),
            pointerEvents: expandedPhase === "settled" ? "auto" : "none",
          }}
          onClick={closeExpandedOverlay}
        />
      )}
      {isOverlayPrewarmed
        && viewportWidth !== null
        && bubbleGraphRenderWidth !== null
        && expandedContentWidth !== null && (
        <HoverSwapsDisabledContext.Provider
          // Hover is not an input on phones (2026-07-09): touch taps used
          // to drive the desktop hover swaps here. All swap providers go
          // inert; taps to the metric DETAIL pages keep working — that's
          // MetricTrigger's click path, independent of the hover system.
          value={true}
        >
        <PolicyLayoutAnimationDisabledContext.Provider
          // While the pane is rising/dragging (or prewarmed hidden), the
          // policy boxes' framer layout tweens would chase the moving
          // surface and swim in after the rest of the panel — they sit out
          // until the sheet RESTS (settled or the half detent).
          value={expandedPhase !== "settled" && expandedPhase !== "half"}
        >
        <BelowDetailHudSwapProviders opts={BELOW_DETAIL_HUD_BODY_OPTS}>
        <div
          className="md:hidden fixed inset-0 z-[60]"
          style={{
            // Full-size and pre-rendered; the visible window is derived
            // from the tween's progress every frame (no CSS transition —
            // the same value also drives the morph graph, so the card and
            // its content can never desync).
            clipPath: overlayClipPath,
            // Transparent root — the opaque backdrop is a separate layer that
            // fades in (below), so the collapsed card behind shows through
            // early in the morph.
            color: props.pageTextColor,
            // Hidden (but mounted and laid out) while closed. Opacity too:
            // visibility is inheritable and a descendant that sets its own
            // "visible" punches through a hidden root — opacity can't be
            // overridden from below, so nothing prewarmed can ghost.
            visibility: expandedPhase === "closed" ? "hidden" : "visible",
            opacity: expandedPhase === "closed" ? 0 : 1,
            pointerEvents: expandedPhase === "closed" ? "none" : "auto",
          }}
          onClickCapture={event => {
            // The strip's tap surfaces open the sheet on pointerup, and the
            // tap's trailing synthetic click then hit-tests this freshly
            // interactive overlay — at that instant clipped to the strip's
            // own rect, where the dashboard graph (a whole-box capability
            // metric trigger) sits. Swallow clicks until the sheet RESTS, so
            // a strip tap only ever opens the dashboard and can never
            // click-through into it (same hazard on a drag release).
            if (expandedPhase !== "settled" && expandedPhase !== "half") {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
        >
          {/* Cream tint that fades in as the window opens, so the morph
              lands on the opaque dashboard. Sheet mode: opaque from the
              first pixel — the pane itself is the surface, nothing blends
              through it. */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundColor: props.pageBackgroundColor,
              opacity: isSheetMode ? 1 : revealOpacity(0.12, 0.6),
            }}
          />
          <div
            ref={overlayScrollerRef}
            className="absolute inset-0 overflow-y-auto overflow-x-hidden"
            style={{
              paddingBottom: isSheetMode
                ? 24
                : "calc(24px + env(safe-area-inset-bottom))",
              // Sheet mode: the scroller IS the card interior — pinned to the
              // full-open card rect (strip width + margins, SHEET_CARD_TOP_GAP
              // below the viewport top) and riding the pane: shifted so its
              // top sits at the pane's rising top edge (the same lerp as the
              // clip's top inset), at rest once fully open.
              ...(isSheetMode
                && overlayOriginRect !== null
                && viewportWidth !== null
                && typeof window !== "undefined"
                ? {
                    top: SHEET_CARD_TOP_GAP_PX,
                    left: overlayOriginRect.left,
                    right: Math.max(
                      0,
                      viewportWidth - overlayOriginRect.left - overlayOriginRect.width,
                    ),
                    bottom: Math.max(
                      0,
                      window.innerHeight - overlayOriginRect.top - overlayOriginRect.height,
                    ),
                    transform: `translateY(${lerp(
                      overlayOriginRect.top - SHEET_CARD_TOP_GAP_PX,
                      0,
                      morphProgress,
                    )}px)`,
                  }
                : {}),
            }}
          >
          {/* No header — the year/perspective it used to show are redundant now
              (the graph carries its own year pill). The close button lives as a
              fixed top-right control (rendered outside this clipped scroller so
              it isn't clipped by the morph). */}
          <div
            className="flex w-full flex-col px-4 pb-6"
            style={{
              // The card already floats below the notch — no safe-area pad.
              paddingTop: isSheetMode ? 14 : "calc(14px + env(safe-area-inset-top))",
            }}
          >
            {/* FLIPs from the bubble's rect into place while the clip opens,
                so the tap reads as the bubble itself growing into the
                dashboard graph. Compositor-only (transform). */}
            <div
              ref={expandedGraphBlockRef}
              className="relative z-10 font-mono text-[12px]"
              style={{ width: expandedContentWidth }}
            >
              {/* Shown only when settled: during the morph the morph graph
                  (below) stands in for it at interpolated geometry, and when
                  closed an explicit "visible" here would punch through the
                  hidden overlay root and ghost over the page. Sheet mode has
                  no morph graph — the real graph rides the pane through
                  every phase (still hidden while closed, same punch-through
                  hazard). */}
              <div
                style={{
                  visibility: (isSheetMode
                    ? expandedPhase !== "closed"
                    : expandedPhase === "settled")
                    ? "visible"
                    : "hidden",
                }}
              >
                {expandedGraphContent}
              </div>
              <div
                className="pointer-events-none"
                style={{
                  // Fades in over the latter part of the morph (by then the
                  // graph geometry is close to its final place, so the globe —
                  // pinned at that final position — lands roughly aligned)
                  // instead of popping in at settle. Sheet mode: solid — it
                  // rides the pane like everything else.
                  opacity: isSheetMode ? 1 : revealOpacity(0.6, 1),
                }}
              >
                {expandedGlobeContent}
              </div>
            </div>
            <div style={{ opacity: isSheetMode ? 1 : revealOpacity(0.15, 0.6) }}>
              {dashboardPanelsContent}
            </div>
          </div>
          </div>
          {/* Generous dismiss zone (sheetDismissZoneHeightPx variants): the
              card's whole top region — full width, down through the top half
              of the graph's dither — swipes the sheet down. Drag-only (a
              stray tap on the graph must not close the card); the pill
              button after it paints on top and keeps tap-to-close. Rides
              the pane's top edge like the pill. */}
          {isSheetMode
            && sheetDragHandle !== null
            && stripConfig !== null
            && stripConfig.sheetDismissZoneHeightPx !== null
            && overlayOriginRect !== null && (
            <button
              type="button"
              aria-label="Drag down to close data dashboard"
              className="absolute cursor-grab border-0 bg-transparent p-0"
              style={{
                top: SHEET_CARD_TOP_GAP_PX,
                left: overlayOriginRect.left,
                width: overlayOriginRect.width,
                height: stripConfig.sheetDismissZoneHeightPx,
                transform: `translateY(${lerp(
                  overlayOriginRect.top - SHEET_CARD_TOP_GAP_PX,
                  0,
                  morphProgress,
                )}px)`,
                touchAction: "none",
              }}
              onPointerDown={event => {
                event.preventDefault()
                beginSheetDismissDrag(event.clientY, false)
              }}
            />
          )}
          {/* Sheet mode: the card's own grabber pill, riding the pane's top
              edge through the whole gesture (same translate as the scroller,
              clipped with the pane). Drag it down to dismiss, tap it to
              close — it replaces the ✕. Hotspot is pill-sized plus padding,
              like the strip's, so it doesn't eat touches across the card. */}
          {isSheetMode
            && sheetDragHandle !== null
            && overlayOriginRect !== null && (
            <button
              type="button"
              aria-label="Drag down or tap to close data dashboard"
              className="absolute flex cursor-grab items-center justify-center border-0 bg-transparent p-0"
              style={{
                // Hotspot centered on the pill's on-graph position.
                top: SHEET_CARD_TOP_GAP_PX
                  + SHEET_CARD_HANDLE_TOP_PX
                  - (SHEET_DRAG_HOTSPOT_HEIGHT_PX - sheetDragHandle.heightPx) / 2,
                left: overlayOriginRect.left
                  + overlayOriginRect.width / 2
                  - (sheetDragHandle.widthPx + SHEET_DRAG_HOTSPOT_WIDTH_EXTRA_PX) / 2,
                width: sheetDragHandle.widthPx + SHEET_DRAG_HOTSPOT_WIDTH_EXTRA_PX,
                height: SHEET_DRAG_HOTSPOT_HEIGHT_PX,
                transform: `translateY(${lerp(
                  overlayOriginRect.top - SHEET_CARD_TOP_GAP_PX,
                  0,
                  morphProgress,
                )}px)`,
                touchAction: "none",
              }}
              onPointerDown={event => {
                event.preventDefault()
                beginSheetDismissDrag(event.clientY)
              }}
            >
              <span
                aria-hidden
                className="rounded-full"
                style={{
                  width: sheetDragHandle.widthPx,
                  height: sheetDragHandle.heightPx,
                  // Knocked out of the graph's dither in the page background,
                  // matching the strip's pill.
                  backgroundColor: props.pageBackgroundColor,
                }}
              />
            </button>
          )}
          {/* Sheet mode's close button (back by request, 2026-07-09): a
              page-background circle with a black ✕ at the card's top-right,
              riding the pane like the pill. Top edge aligned with the pill,
              and inset from the card's right edge by the same
              SHEET_CARD_HANDLE_TOP_PX the pill keeps from the top. The pill
              drag/tap and scrim tap keep working — this is a third,
              explicit close affordance. */}
          {isSheetMode && overlayOriginRect !== null && (
            <button
              type="button"
              aria-label="Close data dashboard"
              // Generous hotspot: the visible circle stays 36px (top-aligned
              // with the pill, 22px right inset) but the tappable button is
              // SHEET_CLOSE_HOTSPOT_PX square, centered on it.
              className="absolute flex items-center justify-center border-0 bg-transparent p-0"
              style={{
                top: SHEET_CARD_TOP_GAP_PX + SHEET_CARD_HANDLE_TOP_PX
                  - (SHEET_CLOSE_HOTSPOT_PX - 36) / 2,
                left: overlayOriginRect.left + overlayOriginRect.width
                  - 36 - SHEET_CARD_HANDLE_TOP_PX
                  - (SHEET_CLOSE_HOTSPOT_PX - 36) / 2,
                width: SHEET_CLOSE_HOTSPOT_PX,
                height: SHEET_CLOSE_HOTSPOT_PX,
                transform: `translateY(${lerp(
                  overlayOriginRect.top - SHEET_CARD_TOP_GAP_PX,
                  0,
                  morphProgress,
                )}px)`,
              }}
              onClick={closeExpandedOverlay}
            >
              <span
                aria-hidden
                className="flex h-9 w-9 items-center justify-center rounded-full"
                style={{
                  backgroundColor: props.pageBackgroundColor,
                  color: props.pageTextColor,
                }}
              >
                <X size={18} strokeWidth={2} />
              </span>
            </button>
          )}
          {/* The morphing graph: re-rendered every tween frame at genuinely
              interpolated geometry (position, size, padding, stroke), so
              each frame is a coherent graph between the bubble's and the
              dashboard's — no scaling artifacts. The bubble's action-fit
              rendering and the dashboard's desktop rendering show different
              viewports, so the morph stacks BOTH at the same interpolated
              geometry and crossfades: identical to the bubble at progress 0
              and to the dashboard graph at progress 1. */}
          {!isSheetMode
            && (expandedPhase === "open"
              || expandedPhase === "closing"
              || expandedPhase === "dragging")
            && overlayOriginRect !== null
            && expandedGraphRect !== null && (() => {
            const morphWidth = lerp(
              overlayOriginRect.width + MOBILE_GRAPH_BLEED_PX * 2,
              expandedGraphRect.width,
              morphProgress,
            )
            const morphHeight = lerp(
              MOBILE_PREVIEW_GRAPH_HEIGHT_PX,
              MOBILE_EXPANDED_GRAPH_HEIGHT_PX,
              morphProgress,
            )
            // Bubble padding (flush dither) to the dashboard graph's
            // EFFECTIVE padding — not the scale-settings defaults: with no
            // override, PlaybookExtrapolatedGraph zeroes right and the
            // render hook floors left at 3 (useExtrapolatedGraphRenderProps).
            const morphScaleOverrides = {
              padding: {
                top: lerp(6, DEFAULT_SCALE_SETTINGS.padding.top, morphProgress),
                right: lerp(MOBILE_GRAPH_BLEED_PX, 0, morphProgress),
                bottom: lerp(
                  8,
                  DEFAULT_SCALE_SETTINGS.padding.bottom,
                  morphProgress,
                ),
                left: lerp(MOBILE_GRAPH_BLEED_PX, 3, morphProgress),
              },
            }
            return (
              <div
                className="pointer-events-none absolute z-10 font-mono text-[12px]"
                style={{
                  top: lerp(
                    overlayOriginRect.top,
                    expandedGraphRect.top,
                    morphProgress,
                  ),
                  left: lerp(
                    overlayOriginRect.left - MOBILE_GRAPH_BLEED_PX,
                    expandedGraphRect.left,
                    morphProgress,
                  ),
                  width: morphWidth,
                  // Crossfade with the collapsed bubble's chart (which shows
                  // through the not-yet-opaque backdrop and fades out over the
                  // same span), so the two graph renderings blend rather than
                  // hard-swap at the start/end of the morph.
                  opacity: revealOpacity(0, 0.4),
                }}
              >
                <div style={{ opacity: 1 - morphProgress }}>
                  <MobileCapabilitiesGraph
                    width={morphWidth}
                    height={morphHeight}
                    scaleOverrides={morphScaleOverrides}
                    showRiskGradient={false}
                  />
                </div>
                <div
                  className="absolute inset-x-0 top-0"
                  style={{ opacity: morphProgress }}
                >
                  <MobileCapabilitiesGraph
                    desktopRendering
                    // Must match the in-flow dashboard graph: hover-reveal
                    // extends the visible loss-of-control projection, which
                    // changes the x-mapping — without it the settle jumps.
                    revealAnnotations
                    showLabels
                    showRiskBackgroundLabels
                    width={morphWidth}
                    height={morphHeight}
                    scaleOverrides={morphScaleOverrides}
                  />
                </div>
              </div>
            )
          })()}
        </div>
        </BelowDetailHudSwapProviders>
        </PolicyLayoutAnimationDisabledContext.Provider>
        </HoverSwapsDisabledContext.Provider>
      )}
      {/* Close control: fixed top-right, outside the clipped overlay so the
          morph never clips it. Fades in/out with the morph like the old header
          did. Sheet mode drops it — the card's pill (drag down or tap) and
          the scrim tap are the close affordances. */}
      {!isSheetMode && expandedPhase !== "closed" && (
        <button
          type="button"
          aria-label="Close data dashboard"
          className="md:hidden fixed z-[70] flex h-9 w-9 items-center justify-center rounded-full"
          style={{
            top: "calc(14px + env(safe-area-inset-top))",
            right: "14px",
            backgroundColor: props.pageTextColor,
            color: props.pageBackgroundColor,
            opacity: revealOpacity(0.05, 0.5),
          }}
          onClick={closeExpandedOverlay}
        >
          <X size={18} strokeWidth={2} />
        </button>
      )}
      {/* Metric detail card (tapping a metric chip in the dashboard). It MUST
          mount inside this HudContextProvider: its live blocks (the power
          treemap/breakdown, capability ladder) read the story year from
          useHudContext(), and outside a provider that falls back to the
          timeline-start year — the panel's numbers froze at the earliest
          keyframe while its header showed the current year (2026-07-10).
          Same hudDate as the provider, so the header year, now-dot, and
          live data all agree — including the overlay's frozen date, which
          is what the dashboard the reader tapped from was showing. */}
      <MobileMetricDetailsPanel
        year={hudDate.getFullYear() + hudDate.getMonth() / 12}
        panelDesign={metricPanelDesign}
      />
    </HudContextProvider>
  )
}
